'use client';

import type {NextWebVitalsMetric} from 'next/app';
import {useReportWebVitals} from 'next/web-vitals';
import {useCallback} from 'react';
import type {WebVitalsMetric} from './types';

export type {WebVitalsMetric};

/**
 * helper to format Next.js metrics into GTM format.
 */
export const formatWebVitalsMetric = (
  metric: NextWebVitalsMetric,
): WebVitalsMetric => {
  return {
    event: 'web_vitals',
    event_category: 'Web Vitals',
    event_action: metric.name,
    event_value: Math.round(
      metric.name === 'CLS' ? metric.value * 1000 : metric.value,
    ),
    event_label: metric.id,
    metric_name: metric.name,
    metric_value: metric.value,
    metric_id: metric.id,
    metric_rating: (metric as NextWebVitalsMetric & {rating?: string}).rating,
    non_interaction: true,
  };
};

/**
 * Hook to report Web Vitals.
 * @param onReport Callback function to handle the formatted metric.
 */
export const useWebVitalsReport = (
  onReport: (metric: WebVitalsMetric) => void,
): void => {
  const onWebVitalEvent = useCallback(
    (metric: NextWebVitalsMetric) => {
      const formattedMetric = formatWebVitalsMetric(metric);
      onReport(formattedMetric);
    },
    [onReport],
  );

  useReportWebVitals(onWebVitalEvent);
};
